import { z } from 'zod';
import { CountryCodeSchema, CurrencySchema, IdSchema, LocaleSchema, RoleSchema } from './common';
import { inspectPassword, PasswordSchema, passwordProblemKey } from './password';

/**
 * Authentication contracts.
 *
 * The sign-in flow is deliberately e-mail-centric: a user types an address
 * and a password, and the server works out which company that address
 * belongs to. The company slug is never asked for — nobody remembers one,
 * and the address already identifies the workspace.
 */

/** A four-digit code, as delivered by e-mail for two-factor sign-in. */
export const VerificationCodeSchema = z
  .string()
  .trim()
  .regex(/^\d{4}$/, { message: 'validation.code4' });

/* ------------------------------------------------------------------ *
 * Login
 * ------------------------------------------------------------------ */

export const LoginRequestSchema = z.object({
  email: z
    .email()
    .transform((value) => value.toLowerCase())
    .describe('Correo del usuario; identifica también su empresa.'),
  password: z.string().min(1).max(200),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

export const AuthUserSchema = z.object({
  id: IdSchema,
  email: z.string(),
  fullName: z.string(),
  role: RoleSchema,
  locale: LocaleSchema,
  tenantId: IdSchema,
  tenantSlug: z.string(),
  /**
   * The workspace's registry name: what provisioning derived from the sign-up
   * address. It is the name the destructive "delete workspace" confirmation
   * compares against, so it is not the one to show in the chrome when the
   * company has chosen its own.
   */
  tenantName: z.string(),
  /**
   * What the company calls itself in the interface — *Settings → Organisation
   * → Display name*. Falls back to {@link tenantName} when unset.
   *
   * Carried on the session rather than read from the settings endpoint
   * because branding is guarded by `settings:write`: a cashier or a viewer
   * may never call it, yet every screen shows the company's name.
   */
  displayName: z.string(),
  /** Whether the account asks for an e-mailed code on every sign-in. */
  twoFactorEnabled: z.boolean(),
});
export type AuthUser = z.infer<typeof AuthUserSchema>;

export const TokenPairSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  /** Access token lifetime in seconds. */
  expiresIn: z.number().int().positive(),
  tokenType: z.literal('Bearer'),
});
export type TokenPair = z.infer<typeof TokenPairSchema>;

export const LoginResponseSchema = TokenPairSchema.extend({
  user: AuthUserSchema,
});
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

/**
 * First half of a two-factor sign-in: the password was right, but the
 * account wants a code. No tokens are issued yet.
 */
export const TwoFactorChallengeSchema = z.object({
  twoFactorRequired: z.literal(true),
  /** Identifies the pending challenge; opaque to the client. */
  challengeId: z.string(),
  /** Partially masked address the code was sent to, e.g. `an***@shop.ru`. */
  emailHint: z.string(),
  expiresInSeconds: z.number().int().positive(),
});
export type TwoFactorChallenge = z.infer<typeof TwoFactorChallengeSchema>;

/** `/auth/login` answers with either a session or a challenge. */
export const LoginResultSchema = z.union([
  LoginResponseSchema,
  TwoFactorChallengeSchema,
]);
export type LoginResult = z.infer<typeof LoginResultSchema>;

/** Second half of a two-factor sign-in: the e-mailed code. */
export const VerifyTwoFactorSchema = z.object({
  challengeId: z.string().min(1),
  code: VerificationCodeSchema,
});
export type VerifyTwoFactor = z.infer<typeof VerifyTwoFactorSchema>;

/* ------------------------------------------------------------------ *
 * Captcha
 * ------------------------------------------------------------------ */

/**
 * Two human-verification backends, and the reason for the order.
 *
 * `altcha` is the default. It is a proof-of-work challenge: the server mints
 * a puzzle, the browser spends a fraction of a second of CPU on it, and the
 * server checks the answer in one hash. Nothing leaves the deployment, there
 * is no account to create and no quota, which is what makes it the only
 * option that also works on a network with no route to the internet.
 *
 * `turnstile` is Cloudflare's widget. It is the stronger of the two, because
 * it actually classifies the visitor instead of merely charging them CPU —
 * but it needs a Cloudflare account, both keys, and a client that can reach
 * `challenges.cloudflare.com`. It is the fallback for a deployment that wants
 * that and can afford it.
 *
 * What is *not* here any more is the arithmetic puzzle ("7 + 5"). It was
 * defeated by one line of `eval` and by any language model, so it cost a real
 * visitor a moment of attention while costing a script nothing. A weak check
 * that looks like a strong one is worse than no check at all.
 */
export const CaptchaProviderSchema = z.enum(['altcha', 'turnstile']);
export type CaptchaProvider = z.infer<typeof CaptchaProviderSchema>;

/**
 * The parameters of a proof-of-work challenge, as minted by `altcha-lib`.
 *
 * Typed rather than passed through as `unknown` so that a version drift
 * between the server library and the browser widget is a compile error on one
 * side of the wire rather than a widget that silently refuses to start.
 */
export const AltchaChallengeParametersSchema = z.object({
  algorithm: z.string().max(40),
  cost: z.number().int().positive(),
  expiresAt: z.number().int().positive().optional(),
  keyLength: z.number().int().positive().optional(),
  keyPrefix: z.string().max(200),
  memoryCost: z.number().int().positive().optional(),
  nonce: z.string().max(200),
  parallelism: z.number().int().positive().optional(),
  salt: z.string().max(200),
});
export type AltchaChallengeParameters = z.infer<typeof AltchaChallengeParametersSchema>;

/** A minted challenge, signed so the client cannot weaken its own puzzle. */
export const AltchaChallengeSchema = z.object({
  parameters: AltchaChallengeParametersSchema,
  signature: z.string().max(200).optional(),
});
export type AltchaChallenge = z.infer<typeof AltchaChallengeSchema>;

/** Handed to the client so it knows which widget to render. */
export const CaptchaChallengeSchema = z.object({
  provider: CaptchaProviderSchema,
  /** Public site key, present when `provider = turnstile`. */
  siteKey: z.string().optional(),
  /** The puzzle to solve, present when `provider = altcha`. */
  challenge: AltchaChallengeSchema.optional(),
});
export type CaptchaChallenge = z.infer<typeof CaptchaChallengeSchema>;

/** What the client sends back for verification. */
export const CaptchaSolutionSchema = z.object({
  provider: CaptchaProviderSchema,
  /** Turnstile response token, when `provider = turnstile`. */
  token: z.string().max(4096).optional(),
  /**
   * The solved proof-of-work, base64 as the ALTCHA widget emits it, when
   * `provider = altcha`.
   *
   * The ceiling is the widget's own payload — a challenge plus a solution —
   * not a token, so it is larger than the Turnstile one.
   */
  payload: z.string().max(8192).optional(),
});
export type CaptchaSolution = z.infer<typeof CaptchaSolutionSchema>;

/* ------------------------------------------------------------------ *
 * Company registration
 * ------------------------------------------------------------------ */

/**
 * Self-service sign-up. Only the mandatory fields are collected: the
 * workspace name and its slug are derived from the address and can be
 * renamed later from the settings screen.
 */
export const RegisterCompanySchema = z
  .object({
    email: z
      .email()
      .transform((value) => value.toLowerCase())
      .describe('Correo del administrador y de la empresa.'),
    password: PasswordSchema,
    confirmPassword: z.string().min(1).max(200),
    /** Unticked by default: two-factor is opt-in. */
    twoFactorEnabled: z.boolean().default(false),
    captcha: CaptchaSolutionSchema,
  })
  .refine((value) => value.password === value.confirmPassword, {
    message: 'validation.passwordMismatch',
    path: ['confirmPassword'],
  })
  .superRefine((value, ctx) => {
    /*
     * The check `PasswordSchema` cannot make on its own: a password built out
     * of the address it protects. `ana@empresa.com` with the password
     * `empresa2026` is the single most predictable choice there is, and the
     * schema that validates the password never sees the address.
     */
    const problem = inspectPassword(value.password, { email: value.email });
    if (problem === 'passwordContainsEmail' || problem === 'passwordContainsName') {
      ctx.addIssue({
        code: 'custom',
        message: passwordProblemKey(problem),
        path: ['password'],
      });
    }
  });
export type RegisterCompany = z.infer<typeof RegisterCompanySchema>;
export type RegisterCompanyInput = z.input<typeof RegisterCompanySchema>;

/**
 * A country the platform can build a workspace in.
 *
 * Derived from the country packs rather than stored anywhere, so adding a pack
 * is what adds an option and there is no second list to keep in step.
 */
export const CountryOptionSchema = z.object({
  code: CountryCodeSchema,
  /** The country's name, in the language the request asked for. */
  name: z.string(),
  /** ISO-4217 code prices will be quoted in. */
  currency: CurrencySchema,
  timezone: z.string(),
  /** Interface language the pack prefers. */
  defaultLocale: z.string(),
  /**
   * False for a pack still being verified against its tax authority.
   *
   * Self-service sign-up offers only ready countries: a customer who picks an
   * unverified one gets invoices their authority may reject. The pack carries
   * the detail — see `CountryPack.unverified`.
   */
  ready: z.boolean(),
});
export type CountryOption = z.infer<typeof CountryOptionSchema>;

/**
 * What sign-up returns: a ticket, not a session.
 *
 * Registration collects an address and a password. The country is chosen on
 * the next screen, and the tenant database is created only once it is known,
 * because the country pack is what decides what gets seeded into it. So this
 * token cannot call a single business route — it is good for choosing a
 * country and nothing else, and the full session arrives with the answer to
 * that choice.
 *
 * The country list travels with it so the picker renders immediately instead
 * of waiting on a second request.
 */
export const OnboardingSessionSchema = z.object({
  /** Proves the sign-up. Not an access token; see above. */
  onboardingToken: z.string(),
  expiresInSeconds: z.number().int().positive(),
  /** The workspace that will exist once a country is chosen. */
  tenantSlug: z.string(),
  email: z.string(),
  /**
   * False while the country is still to be chosen, true once a database has
   * been built. A client that reloaded mid-onboarding uses it to decide
   * whether to show the picker or the app.
   */
  countryChosen: z.boolean(),
  countries: z.array(CountryOptionSchema),
});
export type OnboardingSession = z.infer<typeof OnboardingSessionSchema>;

/** Signing up no longer returns a ready session. See {@link OnboardingSession}. */
export type RegisterCompanyResponse = OnboardingSession;

/** The one thing an onboarding token is allowed to do. */
export const ChooseCountrySchema = z.object({
  country: CountryCodeSchema.describe('Código ISO 3166-1 alpha-2 del país elegido.'),
});
export type ChooseCountry = z.infer<typeof ChooseCountrySchema>;

/** Choosing a country finishes provisioning and returns a real session. */
export type ChooseCountryResponse = LoginResponse;

/** Claims embedded in the onboarding token. */
export const OnboardingTokenClaimsSchema = z.object({
  tid: IdSchema,
  /** Lowercased address the sign-up used; identifies the pending owner row. */
  email: z.string(),
  /** Distinguishes this from a session token, and from a refresh token. */
  typ: z.literal('onboarding'),
});
export type OnboardingTokenClaims = z.infer<typeof OnboardingTokenClaimsSchema>;

/* ------------------------------------------------------------------ *
 * Password recovery
 * ------------------------------------------------------------------ */

export const ForgotPasswordRequestSchema = z.object({
  email: z
    .email()
    .transform((value) => value.toLowerCase())
    .describe('Correo de la cuenta; se responde igual exista o no.'),
  captcha: CaptchaSolutionSchema,
});
export type ForgotPasswordRequest = z.infer<typeof ForgotPasswordRequestSchema>;

/** Always the same answer, so the endpoint cannot be used to enumerate users. */
export const ForgotPasswordResponseSchema = z.object({
  success: z.literal(true),
  message: z.string(),
});
export type ForgotPasswordResponse = z.infer<typeof ForgotPasswordResponseSchema>;

export const ResetPasswordRequestSchema = z
  .object({
    token: z.string().min(10).max(200),
    password: PasswordSchema,
    confirmPassword: z.string().min(1).max(200),
  })
  .refine((value) => value.password === value.confirmPassword, {
    message: 'validation.passwordMismatch',
    path: ['confirmPassword'],
  });
export type ResetPasswordRequest = z.infer<typeof ResetPasswordRequestSchema>;

/**
 * Ask which account a reset link belongs to, before the form is shown.
 *
 * The token travels in the body rather than the query string on purpose: it is
 * a credential, and a query string is written to every access log between the
 * browser and the API.
 */
export const PasswordResetInfoRequestSchema = z.object({
  token: z.string().min(10).max(200),
});
export type PasswordResetInfoRequest = z.infer<typeof PasswordResetInfoRequestSchema>;

/**
 * The account the link belongs to.
 *
 * The page names it so a person can see that the link is for the account they
 * meant. Without it, a link for a different address is indistinguishable from
 * the right one until the password has already been changed — and by then the
 * owner of that account has lost access.
 */
export const PasswordResetInfoSchema = z.object({
  email: z.string().describe('Cuenta a la que pertenece el enlace.'),
});
export type PasswordResetInfo = z.infer<typeof PasswordResetInfoSchema>;

/* ------------------------------------------------------------------ *
 * Two-factor settings
 * ------------------------------------------------------------------ */

export const TwoFactorSettingSchema = z.object({
  enabled: z.boolean().describe('Activa el código de 4 dígitos enviado por correo.'),
});
export type TwoFactorSetting = z.infer<typeof TwoFactorSettingSchema>;

export const TwoFactorStatusSchema = z.object({
  enabled: z.boolean(),
});
export type TwoFactorStatus = z.infer<typeof TwoFactorStatusSchema>;

/* ------------------------------------------------------------------ *
 * Authenticated operations
 * ------------------------------------------------------------------ */

export const ChangePasswordSchema = z.object({
  /*
   * `min(1)`, not the policy. This is the password the account already has,
   * and an account created before the policy existed must still be able to
   * change it — refusing to accept the old one would strand exactly the people
   * the policy is meant to protect.
   */
  currentPassword: z.string().min(1).max(200),
  newPassword: PasswordSchema.describe('Contraseña nueva, de 10 a 200 caracteres.'),
});
export type ChangePassword = z.infer<typeof ChangePasswordSchema>;

/**
 * Confirmation for deleting the caller's own workspace.
 *
 * This is the most destructive operation in the product: it drops the
 * company's entire database — every product, document, stock movement,
 * counterparty and user — and cannot be undone. Three independent gates,
 * because each one catches a different mistake:
 *
 *   • `password` proves the person at the keyboard is the account holder and
 *     not somebody who walked up to an unlocked session. A confirmation
 *     dialog alone would let a passer-by destroy the company.
 *   • `confirmName` must repeat the workspace's own name. An id or a slug is
 *     opaque and easy to act on by reflex; typing the name forces the person
 *     to know which company they are about to erase.
 *   • ownership is enforced server-side from the JWT, never from the body.
 *
 * `confirmName` is compared leniently (trimmed, case-insensitively) because
 * the point is deliberate intent, not typographic precision — failing
 * somebody over a trailing space teaches nothing and only encourages
 * copy-pasting the name from somewhere, which is what this is meant to
 * prevent.
 */
export const DeleteWorkspaceSchema = z.object({
  password: z.string().min(1).max(200),
  confirmName: z.string().trim().min(1).max(200),
});
export type DeleteWorkspace = z.infer<typeof DeleteWorkspaceSchema>;

/** What the workspace deletion reports back, for the sign-out flow. */
export const DeleteWorkspaceResultSchema = z.object({
  slug: z.string(),
  databaseName: z.string(),
});
export type DeleteWorkspaceResult = z.infer<typeof DeleteWorkspaceResultSchema>;

export const RefreshRequestSchema = z.object({
  refreshToken: z
    .string()
    .min(10)
    .describe(
      'Token de refresco vigente; se canjea por un par nuevo y el anterior deja de servir.',
    ),
});
export type RefreshRequest = z.infer<typeof RefreshRequestSchema>;

/** Claims embedded in the access token. */
export const AccessTokenClaimsSchema = z.object({
  sub: IdSchema,
  tid: IdSchema,
  slug: z.string(),
  role: RoleSchema,
  locale: LocaleSchema,
  /** Carried in the token so most requests need no extra user lookup. */
  email: z.string(),
  name: z.string(),
  /** Distinguishes access tokens from refresh tokens. */
  typ: z.literal('access'),
});
export type AccessTokenClaims = z.infer<typeof AccessTokenClaimsSchema>;

export const RefreshTokenClaimsSchema = z.object({
  sub: IdSchema,
  tid: IdSchema,
  typ: z.literal('refresh'),
});
export type RefreshTokenClaims = z.infer<typeof RefreshTokenClaimsSchema>;
